// Direct imports
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { Material } from "@babylonjs/core/Materials/material";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Observer } from "@babylonjs/core/Misc/observable";

import { TraceBehavior } from "./tracebehavior";
import type { SceneObject } from "../core/SceneObject";
import type { BevelData } from "../scenedata/behaviors/beveldata";
import { ValidationError } from "../scenedata/behaviordata";
import { getObjectMeshes } from "../utils/MeshUtils";
import { Logger } from "../utils/Logger";
import { markBakeDirty } from "../loaders/restoreBakedSrc";

/** Max visible chord error (sagitta) on a rounded arc, meters. */
const MAX_SAGITTA = 0.00005;

/**
 * Arc samples per 90°, adaptive to WORLD radius: chord error of a 90° arc
 * split into n chords is r·(1−cos(π/4n)); solving for a fixed 0.05mm error
 * gives n ≈ 78·√r. A constant is wrong in both directions — wasteful on a
 * 12mm chip corner, visibly faceted on a 100mm panel corner.
 */
function arcSegments(worldRadius: number): number {
    if (worldRadius <= MAX_SAGITTA) return 8;
    const n = (Math.PI / 4) / Math.acos(1 - MAX_SAGITTA / worldRadius);
    return Math.min(32, Math.max(8, Math.ceil(n)));
}

type AxisIndex = 0 | 1 | 2;

/** What the original (pre-bevel) geometry looked like. */
interface HostInfo {
    /** Full extent per local axis. */
    extents: [number, number, number];
    /** Geometry center per local axis. */
    center: [number, number, number];
    /** Axis the host is flat along (a plane's normal axis), if any. */
    flatAxis: AxisIndex | null;
    /** Facing sign of a flat host's normals along flatAxis. */
    flatSign: 1 | -1;
}

/**
 * Cyclic (handedness-preserving) permutation mapping generation-space
 * (u, v, w) — w being the extrusion axis — onto local x/y/z slots.
 * Cyclic only: a swap would flip winding.
 */
function axisSlots(w: AxisIndex): [AxisIndex, AxisIndex, AxisIndex] {
    if (w === 2) return [0, 1, 2]; // u=x, v=y, w=z
    if (w === 0) return [1, 2, 0]; // u=y, v=z, w=x
    return [2, 0, 1];              // u=z, v=x, w=y
}

const AXIS_NAME_TO_INDEX: Record<string, AxisIndex> = { x: 0, y: 1, z: 2 };

/**
 * Srcs Bevel supports as hosts: the builtin cube/plane primitives, their
 * export-rewritten paths, and previously-baked bevel assets. Mirrors the
 * recognition in TraceExport.bakeBevelNodes — keep the two in sync.
 */
const SUPPORTED_HOST_SRC = /(?:^|[/:])(?:bevel|primitive)_(?:cube|plane)(?:_[0-9a-f]{8})?\.glb$/;

/** The plane alternative of the above — baked facing +Y (BuiltInPrimitives). */
const PLANE_HOST_SRC = /(?:^|[/:])(?:bevel|primitive)_plane(?:_[0-9a-f]{8})?\.glb$/;

/**
 * Bevel — rounds the corners (and optionally all edges) of the node's
 * primitive.
 *
 * Regenerates the mesh's geometry as a rounded-corner slab (or a flat
 * rounded rect when the host is a plane) spanning the same unit local box
 * as the primitive, so the node's transform keeps carrying the size (the
 * baked, dumb-client-readable form).
 *
 * `cornerRadius` is in METRES (#759), with CSS-pixel semantics: a resized
 * slab keeps its corner SIZE, so neither stretching nor uniform scaling
 * changes the world radius. `pill` asks for half the current smaller side
 * instead — a capsule at whatever size the solver picks — and an absolute
 * radius clamps to that same ceiling rather than self-crossing. Corner arcs
 * are generated elliptically in
 * local space (worldRadius / |scale|) so they render circular under
 * non-uniform scale, and regenerate when the scale changes (layout solve,
 * gizmo resize). Corners round in the plane perpendicular to `axis` —
 * "auto" picks the thinnest world axis; a flat host uses its own normal.
 *
 * `edges: "all"` builds a fully filleted box as the offset surface of an
 * inset core rounded rect: the rim (radius = corner radius clamped to half
 * the thickness) sweeps from the wall into each face, and because the core
 * perimeter already carries the corner arcs, the spherical corner patches
 * fall out of the same sweep.
 *
 * Walls and rims share smooth vertex rings with analytic gradient normals
 * (the fillet is tangent-continuous with the straight edges, so a smooth
 * normal field is exact); in "corners" mode the 90° cap↔wall rim stays
 * hard on purpose.
 *
 * Geometry only — color/opacity belong to Renderer, size to the transform.
 * The original geometry is snapshotted on first apply and restored on
 * detach: in an editing session that returns the sharp primitive; when
 * attached over an already-BAKED slab (re-opening an exported scene) the
 * snapshot IS the baked rounding, and detach returns that — once baked,
 * the src is the rounded mesh and there is no sharp original to recover.
 */
export class Bevel extends TraceBehavior<BevelData> {
    static readonly behaviorType = "Bevel" as const;

    private _mesh: Mesh | null = null;
    private _original: VertexData | null = null;
    private _host: HostInfo | null = null;
    /** Node scale the current geometry was compensated against. */
    private _builtScale: Vector3 | null = null;
    private _scaleObserver: Observer<TransformNode> | null = null;
    private _warnedUnsupportedHost = false;

    constructor(sceneObject: SceneObject, data: BevelData) {
        super(sceneObject, data);
        // Nothing else validates the LOAD path (SceneCreation builds behaviors
        // with `new Ctor(...)`), so the legacy `radius` migrates here —
        // MorphStretch's precedent for `fit`. Re-resolve so attach() reads the
        // rewritten fields.
        this.validate(this.getAuthored());
        this._resolved = this._resolveAll();
    }

    /**
     * Before #759 `radius` was a 0..1 fraction of the smaller in-plane side,
     * and the sandbox wrote it from `card()`/`background()` and template
     * conversion from July on, so scenegen and template scenes in the CMS
     * carry it. `createDefaultData` copies unknown keys through and
     * `cornerRadius` defaults to 0, so such a scene loaded silently sharp and
     * a 0.5 lost its pill. Rewrite to metres against the node's in-plane
     * scale (the thin axis is the extrusion axis, as at render); a fraction
     * of 0.5 or more was a pill. Runs on load and on commit, like `fit`.
     */
    private _migrateLegacyRadius(data: BevelData): void {
        const bag = data as unknown as Record<string, unknown>;
        const legacy = bag.radius;
        if (legacy === undefined) return;
        delete bag.radius;
        if (typeof legacy !== 'number' || !Number.isFinite(legacy) || legacy <= 0) return;
        // createDefaultData has already put cornerRadius: 0 / pill: false on
        // the bag by the time SceneCreation constructs us, so "present" cannot
        // mean "authored" here — only a non-default value can.
        if ((data.cornerRadius ?? 0) > 0 || data.pill === true) return;
        const s = this.sceneObject.transform.scaling;
        const world = [Math.abs(s.x), Math.abs(s.y), Math.abs(s.z)];
        // A plane rounds in its own plane: `extrusionAxis` takes host.flatAxis
        // before the authored axis or the thinnest scale, so at render the
        // in-plane sides are x and z and sy never participates. Pick the same
        // pair here or the metres come out against sides nothing renders.
        const thin = PLANE_HOST_SRC.test(this.sceneObject.src)
            ? 1
            : data.axis && data.axis !== 'auto'
                ? AXIS_NAME_TO_INDEX[data.axis]
                : world.indexOf(Math.min(...world));
        const inPlane = world.filter((_, i) => i !== thin);
        const minSide = Math.min(...inPlane) || 1;
        if (legacy >= 0.5) data.pill = true;
        data.cornerRadius = Math.min(legacy, 0.5) * minSide;
        Logger.warn(`[Bevel] "${this.sceneObject.name}": legacy radius ${legacy} (fraction of the smaller side) → cornerRadius ${data.cornerRadius.toFixed(4)} m${data.pill ? ' + pill' : ''}. Re-save to keep it.`);
    }

    protected validate(data: BevelData): ValidationError[] {
        const errors: ValidationError[] = [];
        this._migrateLegacyRadius(data);
        if (data.cornerRadius !== undefined && (typeof data.cornerRadius !== 'number' || !Number.isFinite(data.cornerRadius) || data.cornerRadius < 0)) {
            errors.push({ field: 'cornerRadius', message: 'cornerRadius must be a non-negative number (meters)', code: 'INVALID_RADIUS' });
        }
        if (data.pill !== undefined && typeof data.pill !== 'boolean') {
            errors.push({ field: 'pill', message: 'pill must be a boolean', code: 'INVALID_PILL' });
        }
        if (data.axis !== undefined && !['auto', 'x', 'y', 'z'].includes(data.axis)) {
            errors.push({ field: 'axis', message: 'Axis must be "auto", "x", "y" or "z"', code: 'INVALID_AXIS' });
        }
        if (data.edges !== undefined && !['corners', 'all'].includes(data.edges)) {
            errors.push({ field: 'edges', message: 'Edges must be "corners" or "all"', code: 'INVALID_EDGES' });
        }
        return errors;
    }

    // Meshes are guaranteed to exist at attach time (all behavior load()
    // calls resolve before attach — same contract Renderer relies on).
    protected onAttach(): void {
        this._apply();
        this._watchNodeScale();
    }

    protected async applyCommit(): Promise<void> {
        this._apply();
    }

    protected onDetach(): void {
        if (this._scaleObserver) {
            this._scaleObserver.remove();
            this._scaleObserver = null;
        }
        if (this._mesh && !this._mesh.isDisposed() && this._original) {
            this._original.applyToMesh(this._mesh);
        }
        this._mesh = null;
        this._original = null;
        this._host = null;
        this._builtScale = null;
    }

    /**
     * Corner compensation — and the auto axis choice — depend on node
     * scale; regenerate when any component changes.
     */
    private _watchNodeScale(): void {
        if (this._scaleObserver || !this.sceneObject?.transform) return;
        this._scaleObserver = this.sceneObject.transform.onAfterWorldMatrixUpdateObservable.add((node) => {
            const s = node.scaling;
            const b = this._builtScale;
            if (b && s.x === b.x && s.y === b.y && s.z === b.z) return;
            // The bake is generated at the node's scale.
            if (b) markBakeDirty(this.sceneObject);
            this._apply();
        });
    }

    private _apply(): void {
        if (!this._hostSupported()) return;
        const mesh = this._resolveMesh();
        const host = this._host;
        if (!mesh || !host) return;

        const scale = this.sceneObject.transform.scaling;
        this._builtScale = scale.clone();

        const vd = computeBevelVertexData(this._resolved, host, scale);
        if (!vd) {
            // No rounding — keep/restore the sharp primitive.
            if (this._original) this._original.applyToMesh(mesh);
            return;
        }
        vd.applyToMesh(mesh);
        // Generated triangles are clockwise; imported meshes may cull counterclockwise.
        if ((mesh.material?.sideOrientation ?? mesh.sideOrientation) === Material.CounterClockWiseSideOrientation) {
            mesh.flipFaces();
        }
        mesh.refreshBoundingInfo();
    }

    /**
     * Bevel replaces geometry WHOLESALE — on a sphere, cylinder, or an
     * imported model that would silently swap the mesh for a rounded slab
     * sized to its bounding box. Only recognized primitive hosts are
     * modified; srcs we don't recognize warn and no-op. A node with no src
     * at all (programmatic/test hosts) is the caller's responsibility.
     */
    private _hostSupported(): boolean {
        const src = this.sceneObject.src;
        if (!src || SUPPORTED_HOST_SRC.test(src)) return true;
        if (!this._warnedUnsupportedHost) {
            this._warnedUnsupportedHost = true;
            Logger.warn(`[Bevel] "${this.sceneObject.name}" src "${src}" is not a cube/plane primitive — Bevel only rounds primitive slabs; leaving the mesh untouched`);
        }
        return false;
    }

    /** The node's primitive mesh (first object mesh), original snapshotted. */
    private _resolveMesh(): Mesh | null {
        if (this._mesh && !this._mesh.isDisposed()) return this._mesh;
        const mesh = getObjectMeshes(this.sceneObject.transform)[0];
        if (!(mesh instanceof Mesh)) {
            Logger.warn(`[Bevel] "${this.sceneObject.name}" has no geometry-bearing mesh to bevel (expected a primitive src)`);
            return null;
        }
        // Clones from a cached container SHARE their Geometry with the
        // container's source mesh — writing beveled vertices in place would
        // restyle every other object with the same src (and corrupt the
        // cache for future instantiations). Split off a private copy before
        // the rest snapshot so all writes stay local to this object.
        if (mesh.geometry && mesh.geometry.meshes.length > 1) {
            mesh.makeGeometryUnique();
        }
        this._mesh = mesh;
        this._original = VertexData.ExtractFromMesh(mesh);
        this._host = analyzeHost(this._original);
        return mesh;
    }
}

/** Rim fillet parameters, local units per axis + arc sample count. */
interface RimSpec { u: number; v: number; w: number; samples: number }

/** The shape parameters Bevel reads (a subset of BevelData). */
export type BevelGeometryParams = Pick<BevelData, 'cornerRadius' | 'pill' | 'axis' | 'edges'>;

/** Primitive host types the export bake supports. */
export type BevelHostPrimitive = 'cube' | 'plane';

/**
 * Pure geometry generation for a Bevel over a builtin primitive host at a
 * given node scale — the same computation the live behavior runs, exposed
 * so the EXPORTER can bake the rounded mesh into an asset (clients without
 * a Bevel implementation then render the baked result; a Bevel-aware
 * editor regenerates over it idempotently from the retained params).
 * Returns null when the params produce no rounding (radius 0).
 *
 * The trace `plane` primitive is baked facing +Y (see BuiltInPrimitives).
 */
export function generateBevelGeometry(
    params: BevelGeometryParams,
    scale: [number, number, number],
    host: BevelHostPrimitive,
): VertexData | null {
    const hostInfo: HostInfo = host === 'plane'
        ? { extents: [1, 0, 1], center: [0, 0, 0], flatAxis: 1, flatSign: 1 }
        : { extents: [1, 1, 1], center: [0, 0, 0], flatAxis: null, flatSign: 1 };
    return computeBevelVertexData(params, hostInfo, { x: scale[0], y: scale[1], z: scale[2] });
}

/**
 * The same rounding for a MEDIA quad host — the unit XY plane Image/Video
 * build their quads from (Babylon's CreatePlane faces −Z; the behaviors'
 * 180° Y turn shows it forward). Flat path only: media quads have no
 * thickness. `radius` is METERS and `scale` the quad's world width/height —
 * the caller folds its fit scaling into these before asking. Since #759 the
 * shared core takes meters too, so this is a plain call: the clamp to half
 * the smaller side lives there, and an oversized value degrades to a pill
 * rather than self-crossing. Returns null when there is nothing to round;
 * the caller keeps its plain plane.
 */
export function generateRoundedQuadGeometry(
    radius: number,
    scale: [number, number],
): VertexData | null {
    // No degenerate-scale guard here any more: the clamp lives in the shared
    // core. Not an exact substitute at zero scale — `worldAt` floors each side
    // at 1e-6, so `half` is 5e-7 and a non-zero radius clamps to a sub-micron
    // pill rather than returning null. Academic at that magnitude, and
    // `Graphic.applyQuadShape` — the only caller — gates on
    // `worldW > 1e-6 && worldH > 1e-6` before it asks. Stated so a future
    // direct caller knows the invariant is theirs, not this function's.
    return computeBevelVertexData(
        { cornerRadius: radius, axis: 'auto', edges: 'corners' },
        { extents: [1, 1, 0], center: [0, 0, 0], flatAxis: 2, flatSign: -1 },
        { x: scale[0], y: scale[1], z: 0 },
    );
}

/**
 * Shared core: params + host analysis + node scale → rounded VertexData,
 * or null when there is nothing to round.
 */
function computeBevelVertexData(
    params: BevelGeometryParams,
    host: HostInfo,
    scale: { x: number; y: number; z: number },
): VertexData | null {
    const w = extrusionAxis(params.axis, host, scale);
    const slots = axisSlots(w);
    const worldAt = (i: AxisIndex) =>
        Math.max(Math.abs([scale.x, scale.y, scale.z][i]) * (host.extents[i] || 1), 1e-6);
    const su = worldAt(slots[0]);
    const sv = worldAt(slots[1]);
    const sw = worldAt(slots[2]);

    // PILL-CLAMP RULE (also in `Graphic.applyQuadShape`, graphic.ts — grep
    // this marker to find both; that site needs `half` before it can decide
    // whether to call this function at all, which is why it is not delegated).
    // Meters → local elliptical extents. `pill` is half the smaller side,
    // and an absolute radius clamps to the same ceiling, so an oversized
    // value degrades to a capsule instead of self-crossing.
    const half = Math.min(su, sv) / 2;
    const cornerWorld = Math.min(params.pill ? half : (params.cornerRadius ?? 0), half);
    if (!(cornerWorld > 1e-7)) return null;
    const rx = Math.min(cornerWorld / su, 0.5);
    const ry = Math.min(cornerWorld / sv, 0.5);

    const flat = host.flatAxis !== null
        ? { coord: host.center[host.flatAxis], sign: host.flatSign }
        : null;

    // "all" also fillets the rim where walls meet the faces. Rim radius
    // is the corner radius clamped to half the thickness — a thin card
    // gets a soft pill edge, a cube gets true rounded edges.
    let rim: RimSpec | null = null;
    if (!flat && params.edges === 'all') {
        const rimWorld = Math.min(cornerWorld, sw / 2);
        if (rimWorld > 1e-7) {
            rim = {
                u: Math.min(rimWorld / su, rx),
                v: Math.min(rimWorld / sv, ry),
                w: Math.min(rimWorld / sw, 0.5),
                samples: arcSegments(rimWorld),
            };
        }
    }

    return buildRounded(rx, ry, arcSegments(cornerWorld), slots, flat, rim);
}

/**
 * The plane to round corners in: a flat host dictates its own normal
 * axis; otherwise the authored axis, else the thinnest world axis (the
 * slab's natural thickness). Ties prefer z.
 */
function extrusionAxis(
    authored: BevelGeometryParams['axis'],
    host: HostInfo,
    scale: { x: number; y: number; z: number },
): AxisIndex {
    if (host.flatAxis !== null) return host.flatAxis;
    if (authored && authored !== 'auto') return AXIS_NAME_TO_INDEX[authored];
    const world = [
        Math.abs(scale.x) * (host.extents[0] || 1),
        Math.abs(scale.y) * (host.extents[1] || 1),
        Math.abs(scale.z) * (host.extents[2] || 1),
    ];
    let best: AxisIndex = 2;
    if (world[1] < world[best]) best = 1;
    if (world[0] < world[best]) best = 0;
    return best;
}

/** Measure the original geometry: extents, center, flatness + facing. */
function analyzeHost(data: VertexData): HostInfo {
    const pos = data.positions ?? [];
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < pos.length; i += 3) {
        for (let a = 0; a < 3; a++) {
            min[a] = Math.min(min[a], pos[i + a]);
            max[a] = Math.max(max[a], pos[i + a]);
        }
    }
    const extents: [number, number, number] = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
    const center: [number, number, number] = [(max[0] + min[0]) / 2, (max[1] + min[1]) / 2, (max[2] + min[2]) / 2];

    let flatAxis: AxisIndex | null = null;
    for (const a of [0, 1, 2] as AxisIndex[]) {
        if (Number.isFinite(extents[a]) && extents[a] < 1e-6) { flatAxis = a; break; }
    }

    let flatSign: 1 | -1 = 1;
    if (flatAxis !== null) {
        const normals = data.normals ?? [];
        let sum = 0;
        for (let i = 0; i < normals.length; i += 3) sum += normals[i + flatAxis];
        flatSign = sum < 0 ? -1 : 1;
    }
    return { extents, center, flatAxis, flatSign };
}

interface PerimeterPoint { u: number; v: number; nu: number; nv: number }

/**
 * Rounded-rect perimeter, counter-clockwise viewed from +w. Four
 * elliptical corner arcs; straight edges fall out of consecutive points.
 * Normals are the ellipse gradient (cos a / rx, sin a / ry): exact for the
 * arcs and, by tangent continuity of the fillet, exact at the
 * straight-edge joins — one smooth shared ring shades correctly. Zero
 * radii degrade to a circular normal sweep around the sharp corner (what
 * turns the rim sweep into spherical corner patches).
 */
function roundedPerimeter(rx: number, ry: number, segments: number, hu = 0.5, hv = 0.5): PerimeterPoint[] {
    const cx = hu - rx, cy = hv - ry;
    const corners: Array<[number, number, number]> = [
        [cx, -cy, -Math.PI / 2], // bottom-right, -90° → 0°
        [cx, cy, 0],             // top-right,      0° → 90°
        [-cx, cy, Math.PI / 2],  // top-left,      90° → 180°
        [-cx, -cy, Math.PI],     // bottom-left,  180° → 270°
    ];
    const points: PerimeterPoint[] = [];
    for (const [ox, oy, start] of corners) {
        for (let i = 0; i <= segments; i++) {
            const a = start + (i / segments) * (Math.PI / 2);
            const cos = Math.cos(a), sin = Math.sin(a);
            const gu = rx > 1e-9 ? cos / rx : cos;
            const gv = ry > 1e-9 ? sin / ry : sin;
            const len = Math.hypot(gu, gv) || 1;
            points.push({ u: ox + rx * cos, v: oy + ry * sin, nu: gu / len, nv: gv / len });
        }
    }
    return points;
}

/**
 * Rounded geometry spanning the unit box: a flat single-sided sheet when
 * `flat` is set; an extruded slab with hard cap edges otherwise; a fully
 * filleted box when `rim` is set.
 *
 * Generated triangles use clockwise winding: their geometric cross product
 * anti-aligns with the outward normal.
 */
function buildRounded(
    rx: number,
    ry: number,
    segments: number,
    slots: [AxisIndex, AxisIndex, AxisIndex],
    flat: { coord: number; sign: 1 | -1 } | null,
    rim: RimSpec | null,
): VertexData {
    const positions: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const push = (u: number, v: number, w: number, nu: number, nv: number, nw: number): number => {
        const p = [0, 0, 0], nrm = [0, 0, 0];
        p[slots[0]] = u; p[slots[1]] = v; p[slots[2]] = w;
        const nl = Math.hypot(nu, nv, nw) || 1;
        nrm[slots[0]] = nu / nl; nrm[slots[1]] = nv / nl; nrm[slots[2]] = nw / nl;
        positions.push(p[0], p[1], p[2]);
        normals.push(nrm[0], nrm[1], nrm[2]);
        uvs.push(u + 0.5, v + 0.5);
        return positions.length / 3 - 1;
    };
    // Cap fan over a vertex ring. CCW perimeter has cross +w, and Babylon
    // wants cross anti-aligned with the normal — the +w cap fans reversed.
    const fan = (center: number, ring: number[], sign: number): void => {
        const n = ring.length;
        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            if (sign > 0) indices.push(center, ring[j], ring[i]);
            else indices.push(center, ring[i], ring[j]);
        }
    };
    // Strip between consecutive rings, ordered from higher w to lower w
    // (same pattern as a front→back wall; matches the winding convention).
    const strip = (a: number[], b: number[]): void => {
        const n = a.length;
        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            indices.push(a[i], a[j], b[j], a[i], b[j], b[i]);
        }
    };
    const finish = (): VertexData => {
        const data = new VertexData();
        data.positions = positions;
        data.normals = normals;
        data.uvs = uvs;
        data.indices = indices;
        return data;
    };

    if (flat) {
        const perimeter = roundedPerimeter(rx, ry, segments);
        const center = push(0, 0, flat.coord, 0, 0, flat.sign);
        const ring = perimeter.map(p => push(p.u, p.v, flat.coord, 0, 0, flat.sign));
        fan(center, ring, flat.sign);
        return finish();
    }

    if (!rim) {
        const perimeter = roundedPerimeter(rx, ry, segments);
        for (const sign of [1, -1]) {
            const center = push(0, 0, 0.5 * sign, 0, 0, sign);
            const ring = perimeter.map(p => push(p.u, p.v, 0.5 * sign, 0, 0, sign));
            fan(center, ring, sign);
        }
        const front = perimeter.map(p => push(p.u, p.v, 0.5, p.nu, p.nv, 0));
        const back = perimeter.map(p => push(p.u, p.v, -0.5, p.nu, p.nv, 0));
        strip(front, back);
        return finish();
    }

    // Fully filleted box: offset surface of the CORE rounded rect — inset
    // by the rim radius in extent AND corner radius, keeping the corner
    // centers concentric with the outer shape. Rings sweep the rim
    // quarter-arc from each face into the wall; because the core perimeter
    // carries the corner arcs (or sharp-corner normal sweeps when the rim
    // consumes them), the spherical corner patches fall out of the ladder.
    const core = roundedPerimeter(
        Math.max(rx - rim.u, 0), Math.max(ry - rim.v, 0), segments,
        0.5 - rim.u, 0.5 - rim.v,
    );
    const rings: number[][] = [];
    const ringAt = (phi: number, side: 1 | -1): number[] =>
        core.map(p => push(
            p.u + p.nu * rim.u * Math.cos(phi),
            p.v + p.nv * rim.v * Math.cos(phi),
            side * (0.5 - rim.w + rim.w * Math.sin(phi)),
            p.nu * Math.cos(phi) / rim.u,
            p.nv * Math.cos(phi) / rim.v,
            side * Math.sin(phi) / rim.w,
        ));
    // Top: φ 90°→0° (w descends from the cap edge to the wall), then the
    // mirrored bottom: φ 0°→90°.
    for (let t = rim.samples; t >= 0; t--) rings.push(ringAt((t / rim.samples) * (Math.PI / 2), 1));
    for (let t = 0; t <= rim.samples; t++) rings.push(ringAt((t / rim.samples) * (Math.PI / 2), -1));
    for (let i = 0; i + 1 < rings.length; i++) strip(rings[i], rings[i + 1]);
    // Caps share the φ=90° rings (normals already ±w there — seamless).
    fan(push(0, 0, 0.5, 0, 0, 1), rings[0], 1);
    fan(push(0, 0, -0.5, 0, 0, -1), rings[rings.length - 1], -1);
    return finish();
}
